import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../lib/utils";

/** Demo `.tag` / `.tag.ok` / `.tag.err` / `.tag.warn` / `.tag.gold`. */
export const statusTagVariants = cva(
  "inline-block rounded-full px-2 py-0.5 font-bold text-[11px] leading-4 whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-secondary text-[#374151] dark:text-secondary-foreground",
        ok: "bg-success-bg text-success",
        err: "bg-danger-bg text-danger",
        warn: "bg-warning-bg text-warning",
        gold: "bg-gold-bg text-gold-fg",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export type StatusTagVariant = NonNullable<VariantProps<typeof statusTagVariants>["variant"]>;

export interface StatusTagProps
  extends React.ComponentProps<"span">,
    VariantProps<typeof statusTagVariants> {}

export function StatusTag({ className, variant, ...props }: StatusTagProps) {
  return (
    <span
      data-slot="status-tag"
      data-variant={variant ?? "default"}
      className={cn(statusTagVariants({ variant }), className)}
      {...props}
    />
  );
}

/** Map common domain statuses onto tag variants (invoice / license / device / verdict). */
export function statusVariant(status: string | null | undefined): StatusTagVariant {
  const s = String(status ?? "").toUpperCase();
  if (["ISSUED", "ACTIVE", "VALID", "GENUINE", "PAID", "PUBLISHED", "OK", "STABLE"].includes(s))
    return "ok";
  if (
    [
      "VOID",
      "REVOKED",
      "TAMPERED",
      "INVALID_CODE",
      "INVALID_LABEL",
      "EXPIRED",
      "FAILED",
      "ERROR",
    ].includes(s)
  )
    return "err";
  if (
    ["CANCELLED", "GRACE", "READ_ONLY", "PENDING", "DRAFT", "WARN", "BETA", "NOT_FOUND"].includes(s)
  )
    return "warn";
  if (["GOLD", "ENTERPRISE", "VIP", "PLATINUM"].includes(s)) return "gold";
  return "default";
}
